import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    faComments, faClipboardCheck, faPhoneSlash, faBell
} from '@fortawesome/free-solid-svg-icons';

import { onSocket } from '../../utils/socket';
import {
    unlockAudio, playPing, notify, flashTitle, stopFlashTitle,
    requestNotificationPermission, claimAlert
} from '../../utils/attention';
import { useCall } from '../../context/CallContext';
import AlertToast from './AlertToast';
import '../../styles/alerts.css';

/**
 * Notifications that arrive wherever you are.
 *
 * The server has always pushed every notification the moment it is written —
 * models/Notification.js emits `notification:new` to the recipient's room from
 * a post-save hook, so a task assigned by a manager and a direct message both
 * reach every open tab within milliseconds. Until this component the client
 * did exactly one thing with that: quietly re-fetched the bell's list. Someone
 * on the Attendance page learned about a new task when they next opened Tasks.
 *
 * A call does not work that way, and that asymmetry is the whole reason this
 * exists. CallContext is mounted on the authenticated shell rather than the
 * chat page precisely so a call can arrive while you are looking at Payroll.
 * This is the same placement and the same idea for everything else: sound, a
 * card in the corner, an OS banner when the tab is hidden, and a flashing tab
 * title when it is not on screen at all.
 *
 * What it deliberately does NOT do is invent new notifications. It renders the
 * ones the server already decided to send, so the existing rules still hold —
 * an @mention and a DM raise a chat notification, ordinary group chatter does
 * not, and a muted conversation raises nothing at all. Changing who gets told
 * about what is a server decision and stays one.
 */

/*
 * Per type: what it looks like, and whether it is worth a sound.
 *
 * Only the three the user is actually waiting on make a noise. Birthdays and
 * payroll arrive for everyone at once on a schedule — a chime for those is a
 * chime the whole office hears simultaneously for something nobody needed to
 * hear about within the second, and the reason people turn notifications off.
 */
const STYLES = {
    CHAT: { icon: faComments, tone: 'chat', sound: true },
    TASK: { icon: faClipboardCheck, tone: 'task', sound: true },
    CALL: { icon: faPhoneSlash, tone: 'call', sound: true }
};
const DEFAULT_STYLE = { icon: faBell, tone: 'system', sound: false };

const TTL_MS = 9000;
const MAX_VISIBLE = 3;

const AlertCenter = () => {
    const [alerts, setAlerts] = useState([]);
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // Read inside a socket handler that is registered once, so it has to be a
    // ref — a handler closed over the first render's pathname would keep
    // suppressing alerts for whatever page the user happened to load on.
    const pathRef = useRef(pathname);
    useEffect(() => { pathRef.current = pathname; }, [pathname]);

    /*
     * Silence while a call is up.
     *
     * The call already owns the user's attention and its own ringtone; a chat
     * ping layered over a ringing phone is noise on top of the one thing they
     * need to hear. The card still appears — it costs nothing and is there
     * when they hang up.
     *
     * Optional chaining because this renders inside CallProvider today, but
     * must not become the reason the shell cannot be mounted without it.
     */
    const inCall = Boolean(useCall()?.isBusy);
    const inCallRef = useRef(inCall);
    useEffect(() => { inCallRef.current = inCall; }, [inCall]);

    // The same notification must not produce two cards. insertMany and save
    // both push, a reconnect can replay, and a duplicate is cheap to refuse.
    const seen = useRef(new Set());

    const dismiss = useCallback((id) => {
        setAlerts((prev) => prev.filter((a) => a.id !== id));
    }, []);

    // The bell's own list is not touched here. utils/notificationActions
    // already re-reads it on this same socket event for every subscriber, and
    // a second forced read would turn one round trip into two.
    const open = useCallback((alert) => {
        if (alert.link) navigate(alert.link);
    }, [navigate]);

    /* --------------------------- permission --------------------------- */

    /**
     * Ask for OS notifications only after they have seen why, and only from a
     * gesture.
     *
     * Same rule as calls (utils/attention.js): never on page load. Asked on
     * arrival people decline permanently; asked just after a notification they
     * watched slide in, they say yes. A gesture is also a hard browser
     * requirement in Chrome, so this waits for the next click either way.
     */
    const armed = useRef(false);
    const armPermissionPrompt = useCallback(() => {
        if (armed.current) return;
        if (!('Notification' in window) || Notification.permission !== 'default') return;
        armed.current = true;
        const ask = () => requestNotificationPermission();
        window.addEventListener('pointerdown', ask, { once: true });
    }, []);

    /* ----------------------------- the tab ----------------------------- */

    // A title left flashing after the user has come back and read the thing is
    // worse than never having flashed it.
    useEffect(() => {
        const onVisible = () => { if (!document.hidden) stopFlashTitle(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            stopFlashTitle();
        };
    }, []);

    /* ---------------------------- the feed ---------------------------- */

    useEffect(() => {
        // Idempotent, and cheap. CallProvider does this too, but the shell
        // must still be able to chime if calls are ever mounted elsewhere.
        unlockAudio();

        return onSocket('notification:new', (doc) => {
            if (!doc?._id) return;

            const id = String(doc._id);
            if (seen.current.has(id)) return;
            seen.current.add(id);
            // Bounded: a tab left open all week would otherwise accumulate
            // every id it has ever been sent.
            if (seen.current.size > 200) {
                seen.current = new Set([...seen.current].slice(-100));
            }

            const link = typeof doc.link === 'string' ? doc.link : '';

            /*
             * Already looking at it.
             *
             * One rule covers every type, because `link` is the in-app route
             * the notification points at: a message in the conversation that
             * fills the screen, or a comment on the task page they are reading,
             * needs no card telling them it happened.
             */
            if (link && link.split('?')[0] === pathRef.current) return;

            const style = STYLES[doc.type] || DEFAULT_STYLE;

            setAlerts((prev) => [
                ...prev.slice(-(MAX_VISIBLE - 1)),
                {
                    id,
                    title: doc.title || 'Notification',
                    message: doc.message || '',
                    link,
                    tone: style.tone,
                    icon: style.icon,
                    ttl: TTL_MS
                }
            ]);

            /*
             * Sound and OS banner are per-person, not per-tab: three tabs open
             * on the portal is normal and must not mean three chimes. One claim
             * covers both.
             */
            const mine = claimAlert(`notif-${id}`);

            if (mine && style.sound && !inCallRef.current) playPing();

            if (mine && document.hidden) {
                flashTitle(doc.title || 'New notification');
                notify({
                    title: doc.title || 'Notification',
                    body: (doc.message || '').slice(0, 160),
                    tag: `notif-${id}`,
                    onClick: () => { if (link) navigate(link); }
                });
            }

            armPermissionPrompt();
        });
    }, [navigate, armPermissionPrompt]);

    if (!alerts.length) return null;

    return (
        <div className="app-alert-stack" aria-live="polite" aria-label="Notifications">
            {alerts.map((alert) => (
                <AlertToast
                    key={alert.id}
                    alert={alert}
                    onOpen={open}
                    onDismiss={dismiss}
                />
            ))}
        </div>
    );
};

export default AlertCenter;
